/**
 * PAGE: PUBLIC FORM
 * Page publique pour soumettre un formulaire
 * Accessible sans authentification via /public/forms/:slug
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import { BedWizard } from '../components/bed/BedWizard';
import type { RadixSelectOption } from '../components/ui';
import { Button, PhoneInputIframe, RadixSelect, SchoolAutocomplete, toast } from '../components/ui';
import { useParentFrameHeight } from '../hooks/useParentFrameHeight';
import {
  notifyParentBedPaymentReady,
  notifyParentFormStarted,
  notifyParentLeadConversion,
  notifyParentPaymentReady,
  notifyParentScrollToTop,
} from '../lib/analytics';
import { extractAttributionFromSearch } from '../lib/attribution';
import { buildBedPrefillValues, getBedPrefill } from '../lib/bed/prefill';
import {
  applyCopilotRules,
  hasCopilotUnknownOption,
  isCopilotIncompleteError,
  isGuardedForm,
  isSubmissionValueInvalidError,
  localToday,
  withCopilotChecks,
} from '../lib/copilot-rules';
import { extractLeadPii } from '../lib/lead-pii';
import { isOver28AtEventDate } from '../lib/age-utils';
import { createBedPaymentCase } from '../lib/payment/create-bed-payment-case';
import { createPaymentCase } from '../lib/payment/create-payment-case';
import { pollContactIds } from '../lib/payment/poll-contact-ids';
import { publicSupabase } from '../lib/supabase';
import { cn } from '../lib/utils';
import { createDynamicFormSchema } from '../schemas/form';
import type {
  FieldTemplate,
  Form,
  FormField,
  FormFieldWithValues,
  TargetRole,
} from '../types/form';
import { mergeFieldWithTemplate } from '../types/form';
import { COUNTRIES } from '../types/school';
import { DerogationNotice } from '../components/forms/DerogationNotice';

/**
 * Durée de cooldown anti-doublon (5 minutes en millisecondes)
 * Empêche les re-soumissions rapides du même formulaire
 */
const SUBMISSION_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Obtient le label lisible pour une option de select
 * Gère spécifiquement les codes pays (FRA -> France)
 */
function getOptionLabel(option: string, contactFieldMap?: string): string {
  // Si c'est un champ pays, convertir le code en label
  if (contactFieldMap === 'country') {
    const country = COUNTRIES.find((c) => c.code === option);
    return country?.label || option;
  }
  return option;
}

/**
 * Délimiteur affiché avant le premier champ copilote des formulaires duo :
 * indique que la section copilote peut être remplie de suite ou plus tard.
 * Rendu en V2 seulement : en mode historique, le site affiche l'iframe dans un
 * cadre de hauteur fixe, et chaque ligne ajoutée y pousse le bouton Envoyer
 * hors de la zone visible.
 */
function CopilotSectionDivider() {
  return (
    <div className="border-t border-[#FF0035] pt-5">
      <p className="text-base font-medium text-zinc-900 sm:text-sm">Votre copilote</p>
      <p className="mt-1 text-sm text-zinc-500">
        Si vous avez déjà un ou une copilote, renseignez ses informations ci-dessous (sinon, vous
        pourrez l'ajouter plus tard).
      </p>
    </div>
  );
}

/**
 * Vérifie si le copilote a au moins prénom + email renseignés
 */
function hasCopilotData(data: Record<string, unknown>, fields: FormFieldWithValues[]): boolean {
  const copilotFields = fields.filter((f) => f.target_role === 'copilot');
  const emailField = copilotFields.find((f) => f.contact_field_map === 'email');
  const firstNameField = copilotFields.find((f) => f.contact_field_map === 'first_name');

  const hasEmail = emailField && data[emailField.id]?.toString().trim();
  const hasFirstName = firstNameField && data[firstNameField.id]?.toString().trim();

  return !!(hasEmail && hasFirstName);
}

/**
 * Extrait les données pour un rôle spécifique (pilot, copilot, ou commun)
 */
function extractDataByRole(
  data: Record<string, unknown>,
  fields: FormFieldWithValues[],
  targetRole: TargetRole
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  fields.forEach((field) => {
    // Inclure les champs du rôle ciblé + les champs communs (target_role = null)
    const isTargetRole = field.target_role === targetRole;
    const isCommonField = field.target_role === null || field.target_role === undefined;

    if (isTargetRole || isCommonField) {
      if (data[field.id] !== undefined) {
        result[field.id] = data[field.id];
      }
    }
  });

  return result;
}

export function PublicFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  // Mode plein écran (pour intégration iframe legacy)
  const isFullscreen = searchParams.get('fullscreen') === 'true';

  // Mode iframe : masque les toasts et affiche le message de succès inline
  const isIframeMode = searchParams.get('mode') === 'iframe';
  // V2 responsive demandée explicitement (`v=2`), puis confirmée par le site
  // (poignée de main, origine vérifiée) : sans réponse du site, rendu historique.
  // Un site ou un CRM sans V2 garde ainsi l'affichage actuel, dans les deux
  // ordres de sortie.
  const isResponsiveIframeMode = useParentFrameHeight(
    isIframeMode && searchParams.get('v') === '2'
  );

  // Avant le rendu (layout) : pas d'image avec la racine historique en vw.
  useLayoutEffect(() => {
    if (!isResponsiveIframeMode) return;

    document.documentElement.classList.add('public-form-v2');
    return () => document.documentElement.classList.remove('public-form-v2');
  }, [isResponsiveIframeMode]);

  // BED : lien tokenisé par équipage (?token=). Sert au pré-remplissage
  // (get_bed_prefill) ET à la création du dossier de paiement du solde à la
  // soumission (create_bed_payment_case_from_submission).
  const bedAccessToken = searchParams.get('token');

  const [form, setForm] = useState<Form | null>(null);
  const [productStartDate, setProductStartDate] = useState<string | null>(null);
  // Track dismissed derogation warnings per field (keyed by fieldId → dismissed birthdate value)
  const [dismissedDerogationValues, setDismissedDerogationValues] = useState<
    Record<string, string>
  >({});
  const [fields, setFields] = useState<FormFieldWithValues[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  // BED : erreur de la chaîne paiement du solde (aucun BPI parent payé / échec init).
  const [bedError, setBedError] = useState<'no_parent' | 'failed' | null>(null);
  // Protection anti-doublon : empêche les clics multiples pendant le traitement
  const [isProcessing, setIsProcessing] = useState(false);
  // TICKET-PRE28-02 : « Je ne connais pas encore mon copilote » (non envoyée au
  // CRM) et refus serveur : copilote incomplet ou invalide (copilot_incomplete),
  // date de naissance ou email invalide (submission_value_invalid).
  const [copilotUnknown, setCopilotUnknown] = useState(false);
  const [serverRefusal, setServerRefusal] = useState<'copilot' | 'value' | null>(null);
  // Position du bouton au moment de la soumission (pour mode iframe)
  const [submitButtonPosition, setSubmitButtonPosition] = useState(0);
  // Ref sur le bouton Envoyer pour capturer sa position
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  // Attribution marketing (utm_*, click ids) transmise par le site dans le
  // src de l'iframe — figée au premier rendu, stockée avec chaque soumission
  const attributionRef = useRef(extractAttributionFromSearch(window.location.search));
  // Garde one-shot : le postMessage FORM_STARTED n'est envoyé qu'une seule fois par session de formulaire
  const formStartedSentRef = useRef<boolean>(false);

  // Clé localStorage pour la protection anti-doublon par formulaire
  const cooldownKey = `form_submitted_${slug}`;

  /**
   * Vérifie si une soumission récente existe (protection anti-doublon)
   * Retourne true si le formulaire a été soumis dans les 5 dernières minutes
   */
  const hasRecentSubmission = useCallback((): boolean => {
    try {
      const lastSubmit = localStorage.getItem(cooldownKey);
      if (lastSubmit) {
        const elapsed = Date.now() - parseInt(lastSubmit, 10);
        return elapsed < SUBMISSION_COOLDOWN_MS;
      }
    } catch {
      // localStorage indisponible (navigation privée, etc.)
    }
    return false;
  }, [cooldownKey]);

  /**
   * Enregistre le timestamp de soumission dans localStorage
   */
  const recordSubmission = useCallback(() => {
    try {
      localStorage.setItem(cooldownKey, Date.now().toString());
    } catch {
      // localStorage indisponible
    }
  }, [cooldownKey]);

  /**
   * Efface le timestamp de soumission (pour permettre une nouvelle soumission)
   */
  const clearSubmissionRecord = useCallback(() => {
    try {
      localStorage.removeItem(cooldownKey);
    } catch {
      // localStorage indisponible
    }
  }, [cooldownKey]);

  // Vérifier au chargement si une soumission récente existe (protection anti-doublon)
  useEffect(() => {
    if (hasRecentSubmission()) {
      setSubmitted(true);
    }
  }, [hasRecentSubmission]);

  // Charger le formulaire et ses champs
  useEffect(() => {
    async function loadForm() {
      if (!slug) return;

      try {
        setLoading(true);
        setError(null);

        // Charger le formulaire (utilise la clé anon directement)
        const { data: formData, error: formError } = await publicSupabase
          .from('forms')
          .select('*, product:products(id, name, code, start_date)')
          .eq('slug', slug)
          .eq('is_active', true)
          .single();

        if (formError) {
          console.error('Erreur chargement formulaire:', formError);
          throw formError;
        }

        if (!formData) {
          console.error('Formulaire non trouvé pour slug:', slug);
          setError('Formulaire introuvable');
          return;
        }

        setForm(formData as Form);

        if (formData.product?.start_date) {
          setProductStartDate(formData.product.start_date);
        }

        // Charger les champs avec leurs templates
        const { data: fieldsData, error: fieldsError } = await publicSupabase
          .from('form_fields')
          .select('*, field_template:field_templates(*)')
          .eq('form_id', formData.id)
          .order('order_index', { ascending: true });

        if (fieldsError) {
          console.error('Erreur chargement champs:', fieldsError);
          throw fieldsError;
        }

        // Merger les champs avec leurs templates
        const mergedFields = (fieldsData || []).map((field) =>
          mergeFieldWithTemplate(field as FormField & { field_template: FieldTemplate | null })
        );

        setFields(mergedFields);
      } catch (err) {
        console.error('Error loading form:', err);
        setError('Erreur lors du chargement du formulaire');
      } finally {
        setLoading(false);
      }
    }

    loadForm();
  }, [slug]);

  // TICKET-PRE28-02 : sur un formulaire duo de pré-inscription, le copilote est
  // soit complet (champs obligatoires en miroir du pilote), soit absent (case
  // cochée : champs retirés du schéma, donc ni validés ni envoyés — Zod ne
  // garde que les clés du schéma). Même règle côté serveur (00315/00316).
  const copilotOptionEnabled = hasCopilotUnknownOption(form, fields);
  const copilotHidden = copilotOptionEnabled && copilotUnknown;
  const validatedFields = useMemo(
    () => (copilotOptionEnabled ? applyCopilotRules(fields, copilotUnknown) : fields),
    [copilotOptionEnabled, copilotUnknown, fields]
  );
  const requiredFieldIds = useMemo(
    () => new Set(validatedFields.filter((f) => f.is_required).map((f) => f.id)),
    [validatedFields]
  );

  // Créer le schéma de validation dynamique. Formulaire duo de pré-inscription :
  // contrôles des mêmes règles que le serveur (copilote obligatoire non vide,
  // date de naissance, email ; pilote et copilote), affichés au même clic.
  const baseSchema = createDynamicFormSchema(validatedFields);
  const guardedForm = isGuardedForm(form);
  const formSchema = guardedForm
    ? withCopilotChecks(baseSchema, fields, validatedFields, copilotUnknown, localToday())
    : baseSchema;

  const {
    register,
    handleSubmit,
    setValue,
    setError: setFieldError,
    clearErrors,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
  });

  // BED — pré-remplissage depuis le lien tokenisé (brief §3). Quand un token
  // valide est présent, on résout les données CRM déjà connues de l'équipage
  // (get_bed_prefill, SECURITY DEFINER, scoping strict) et on pré-remplit les
  // champs correspondants. Token invalide/expiré → form vierge (saisie manuelle
  // possible, on ne bloque pas). Les champs inconnus restent vides.
  // Le wizard BED (branche `generates_bed` ci-dessous) fait son propre
  // pré-remplissage via `get_bed_bootstrap` : on n'alimente react-hook-form que
  // pour le rendu à plat (formulaire tokenisé sans `generates_bed`).
  useEffect(() => {
    if (!bedAccessToken || fields.length === 0 || form?.generates_bed) return;
    let cancelled = false;
    (async () => {
      const res = await getBedPrefill(bedAccessToken);
      if (cancelled || res.status !== 'ok') return;
      const values = buildBedPrefillValues(fields, res.prefill);
      Object.entries(values).forEach(([fieldId, value]) => {
        setValue(fieldId, value, { shouldValidate: false });
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [bedAccessToken, fields, setValue, form?.generates_bed]);

  function handleFormFirstInteraction() {
    if (formStartedSentRef.current || !form) return;
    formStartedSentRef.current = true;
    notifyParentFormStarted({ formName: form.name, productName: form.product?.name });
  }

  async function onSubmit(data: Record<string, unknown>) {
    if (!form) return;

    // Protection anti-doublon : empêcher les soumissions multiples
    if (isProcessing) {
      console.warn('[PublicForm] Soumission ignorée : traitement en cours');
      return;
    }

    // Vérifier si une soumission récente existe déjà
    if (hasRecentSubmission()) {
      console.warn('[PublicForm] Soumission ignorée : formulaire déjà soumis récemment');
      setSubmitted(true);
      return;
    }

    // Nouvel envoi : l'ancien bandeau de refus serveur n'a plus lieu d'être.
    setServerRefusal(null);

    // Item #2 : si "je ne suis plus étudiant" est coché, profession devient
    // obligatoire (validation additionnelle hors Zod car pilotée côté UI).
    let professionMissing = false;
    for (const [roleKey, field] of Object.entries(studentFieldsByRole)) {
      // `data` est la sortie parsée du resolver Zod : les checkbox y sont
      // converties en booléen — la comparaison chaîne seule ne matche plus.
      const isNotStudent = data[field.id] === true || data[field.id] === STUDENT_TRUE_VALUE;
      if (!isNotStudent) continue;
      const professionField = professionFieldsByRole[roleKey];
      if (!professionField) continue;
      const professionValue = String(data[professionField.id] || '').trim();
      if (professionValue === '') {
        setFieldError(professionField.id, {
          type: 'manual',
          message: `${professionField.label} est requis si vous n'êtes plus étudiant(e)`,
        });
        professionMissing = true;
      } else {
        clearErrors(professionField.id);
      }
    }
    if (professionMissing) {
      return;
    }

    setIsProcessing(true);

    // En mode iframe, capturer la position réelle du bouton dans le document
    // (l'iframe couvre toute la hauteur, donc pas de scroll interne)
    if (isIframeMode && submitButtonRef.current) {
      const buttonRect = submitButtonRef.current.getBoundingClientRect();
      // Position absolue du bouton dans le document
      setSubmitButtonPosition(buttonRect.top + window.scrollY);
    }

    try {
      const isDuoForm = form.form_type === 'duo';
      const submissions: Array<{ id: string; data: Record<string, unknown> }> = [];

      if (isDuoForm) {
        // Formulaire duo : créer une soumission pour le pilote
        const pilotData = extractDataByRole(data, fields, 'pilot');

        // 00192 : passe par la RPC SECURITY DEFINER — anon n'écrit plus
        // directement dans form_submissions (cf. en-tête de la migration).
        // 00265 : variante qui pose aussi l'attribution marketing.
        const { data: pilotRows, error: pilotError } = await publicSupabase.rpc(
          'submit_public_form_with_attribution',
          { p_form_id: form.id, p_data: pilotData, p_attribution: attributionRef.current }
        );

        if (pilotError) {
          console.error('Erreur soumission pilote:', pilotError);
          throw pilotError;
        }

        submissions.push({ id: pilotRows[0].id, data: pilotData });

        // Si le copilote est renseigné, créer une deuxième soumission. Case
        // « je ne connais pas encore mon copilote » cochée : jamais de
        // soumission copilote, même si des valeurs avaient été saisies avant.
        if (!copilotHidden && hasCopilotData(data, fields)) {
          const copilotData = extractDataByRole(data, fields, 'copilot');

          const { data: copilotRows, error: copilotError } = await publicSupabase.rpc(
            'submit_public_form_with_attribution',
            { p_form_id: form.id, p_data: copilotData, p_attribution: attributionRef.current }
          );

          if (copilotError) {
            console.error('Erreur soumission copilote:', copilotError);
            throw copilotError;
          }

          submissions.push({ id: copilotRows[0].id, data: copilotData });
        }
      } else {
        // Formulaire simple : une seule soumission
        const { data: rows, error: submissionError } = await publicSupabase.rpc(
          'submit_public_form_with_attribution',
          { p_form_id: form.id, p_data: data, p_attribution: attributionRef.current }
        );

        if (submissionError) {
          console.error('Erreur soumission:', submissionError);
          throw submissionError;
        }

        submissions.push({ id: rows[0].id, data });
      }

      // Envoyer une notification email pour chaque soumission (async, ne bloque pas)
      submissions.forEach((submission) => {
        publicSupabase.functions
          .invoke('notify-form-submission', {
            body: {
              form_id: form.id,
              submission_id: submission.id,
              submission_data: submission.data,
            },
          })
          .then(({ error }) => {
            if (error) {
              console.warn("Erreur lors de l'envoi de la notification email:", error);
            }
          })
          .catch((err) => {
            console.warn("Erreur lors de l'appel de la fonction de notification:", err);
          });
      });

      // Si le formulaire génère un BPI, créer le PDF et envoyer l'email de confirmation
      if (form.generates_bpi && submissions.length > 0) {
        // Polling : attendre que le trigger Supabase ait créé les contacts (contact_id non null)
        const pollResult = await pollContactIds(submissions.map((s) => s.id));

        if (pollResult.status !== 'ready') {
          // Les form_submissions existent déjà en DB (anti-doublon préservé ci-dessous),
          // mais le paiement ne peut pas être initialisé : timeout trigger CRM ou erreur DB.
          // On affiche explicitement paymentFailed pour diriger le prospect vers le support.
          setPaymentFailed(true);
        } else {
          // Duo : passer l'id de la submission copilote à la RPC pour poser le
          // même bpi_code sur les deux prospects. L'équipage sera créé plus tard
          // au passage paid, pas à la soumission.
          const copilotSubmissionId =
            isDuoForm && submissions.length === 2 ? submissions[1].id : undefined;

          // Code client unifié (décision 2026-04-19, migration 00071) : la RPC
          // peuple form_submissions.bpi_code avec le format Sandra `9XXYYYZZZZ`.
          // On l'attend AVANT generate-bpi pour que le PDF affiche le code Sandra
          // et pas un code alphanum legacy. L'appel PDF + email reste async.
          // Re-soumission (TICKET-PAY-14, migration 00133) : si un dossier BPI
          // existe déjà pour ce contact/produit, la RPC le réutilise (le tunnel
          // s'ouvre sur le dossier en cours) ou signale `already_paid`.
          const paymentCaseResult = await createPaymentCase({
            formSubmissionId: submissions[0].id,
            copilotSubmissionId,
          });

          if (paymentCaseResult.status === 'already_paid') {
            // Dossier déjà réglé : message dédié (pas un échec — ne pas
            // diriger vers le support paiement ni ouvrir le tunnel).
            setAlreadyPaid(true);
          } else if (paymentCaseResult.status === 'copilot_incomplete') {
            // Refus serveur (00316) : le formulaire reste affiché avec un
            // message lisible pour compléter le copilote ou cocher la case.
            setServerRefusal('copilot');
            return;
          } else if (paymentCaseResult.status === 'ok') {
            const { paymentCaseId } = paymentCaseResult;
            // SEC : generate-bpi + send-bpi-email ne reçoivent plus que
            // l'identifiant du dossier. Destinataires, PII, URL du PDF ET nom de
            // produit sont résolus SERVEUR depuis le payment_case (le body public
            // ne peut plus imposer une adresse, un lien, ni du HTML arbitraire).
            publicSupabase.functions
              .invoke('generate-bpi', {
                body: {
                  payment_case_id: paymentCaseId,
                },
              })
              .then(({ data: bpiResult, error: bpiError }) => {
                if (bpiError) {
                  console.warn('Erreur génération BPI:', bpiError);
                  return;
                }

                if (bpiResult?.success) {
                  publicSupabase.functions
                    .invoke('send-bpi-email', {
                      body: {
                        payment_case_id: paymentCaseId,
                      },
                    })
                    .catch((err) => {
                      console.warn("Erreur lors de l'envoi de l'email BPI:", err);
                    });
                }
              })
              .catch((err) => {
                console.warn('Erreur lors de la génération du BPI:', err);
              });

            notifyParentPaymentReady({
              paymentCaseId,
              product: form.product?.code ?? '',
            });
          } else {
            setPaymentFailed(true);
          }
        }
      }

      // Si le formulaire est un BED, créer le dossier de paiement du SOLDE
      // (branche symétrique de generates_bpi). Le token porte l'équipage + le BPI
      // parent ; la RPC anon délègue tout le calcul financier au socle Victor.
      // Pas de polling contact_id : la RPC n'en dépend pas (résolution par token).
      if (form.generates_bed && bedAccessToken && submissions.length > 0) {
        const bedResult = await createBedPaymentCase({
          formSubmissionId: submissions[0].id,
          bedAccessToken,
        });

        if (bedResult.status === 'ok') {
          notifyParentBedPaymentReady({
            paymentCaseId: bedResult.data.payment_case_id,
            product: form.product?.code ?? bedResult.data.product_code ?? '',
            bedToken: bedResult.data.bed_token,
            orderId: bedResult.data.order_id,
          });
        } else if (bedResult.status === 'no_paid_bpi_parent') {
          setBedError('no_parent');
        } else {
          setBedError('failed');
        }
      }

      // Enregistrer la soumission dans localStorage (protection anti-doublon)
      recordSubmission();
      setSubmitted(true);
      // V2 : le cadre va raccourcir à la taille de l'écran « Merci ! » ; le
      // site remonte au haut du formulaire pour que le message soit à l'écran.
      if (isResponsiveIframeMode) {
        notifyParentScrollToTop();
      }

      // Notifier le site parent pour le tracking (Meta Pixel + GA4).
      // On enrichit avec la PII du pilote en clair : le parent hashe côté serveur
      // (advanced matching Meta CAPI / GA4 MP). Le CRM ne fait aucun hash/appel réseau.
      notifyParentLeadConversion({
        formName: form.name,
        productName: form.product?.name,
        ...extractLeadPii(data, fields),
      });

      // En mode iframe, pas de toast (affichage inline à la place)
      if (!isIframeMode) {
        toast.success('Formulaire envoyé avec succès !');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      // Refus serveur (trigger 00315) : message lisible affiché dans le
      // formulaire, y compris en mode iframe.
      if (isCopilotIncompleteError(err)) {
        setServerRefusal('copilot');
        return;
      }
      if (isSubmissionValueInvalidError(err)) {
        setServerRefusal('value');
        return;
      }
      // En mode iframe, pas de toast d'erreur non plus
      if (!isIframeMode) {
        toast.error("Erreur lors de l'envoi du formulaire");
      }
    } finally {
      setIsProcessing(false);
    }
  }

  /**
   * Réinitialise le formulaire pour permettre une nouvelle soumission
   * Efface les champs et le cooldown anti-doublon
   */
  function handleNewSubmission() {
    reset(); // Vider tous les champs du formulaire
    clearSubmissionRecord(); // Autoriser une nouvelle soumission
    setPaymentFailed(false);
    setAlreadyPaid(false);
    setBedError(null);
    setCopilotUnknown(false);
    setServerRefusal(null);
    setSubmitted(false);
  }

  // =========================================================================
  // ITEM #2 — Logique étudiant / profession (par rôle pour les formulaires duo)
  // =========================================================================
  // - Le champ `is_not_student` est exposé comme une checkbox "Je ne suis plus
  //   étudiant(e)". Sa valeur reste stockée en texte ("Jeune Actif" / "Étudiant")
  //   pour rester compatible avec le trigger SQL `create_contact_from_submission`.
  // - Si la checkbox est cochée → le champ école du même rôle est masqué et son
  //   contenu vidé ; le champ profession (socio_professional_category) du même
  //   rôle devient visuellement requis et validé manuellement à la soumission.
  // - Si la checkbox est décochée → l'école est visible (rendue en autocomplete
  //   via SchoolAutocomplete, item #3), profession reste optionnelle.

  const STUDENT_TRUE_VALUE = 'Jeune Actif';
  const STUDENT_FALSE_VALUE = 'Étudiant';

  const NEWSLETTER_TRUE_VALUE = 'true';
  const NEWSLETTER_FALSE_VALUE = 'false';

  const getRoleKey = (field: FormFieldWithValues) => field.target_role || 'common';

  const studentFieldsByRole = useMemo(() => {
    const map: Record<string, FormFieldWithValues> = {};
    fields.forEach((f) => {
      if (f.contact_field_map === 'is_not_student') {
        map[getRoleKey(f)] = f;
      }
    });
    return map;
  }, [fields]);

  const schoolFieldsByRole = useMemo(() => {
    const map: Record<string, FormFieldWithValues> = {};
    fields.forEach((f) => {
      if (f.contact_field_map === 'school') {
        map[getRoleKey(f)] = f;
      }
    });
    return map;
  }, [fields]);

  const professionFieldsByRole = useMemo(() => {
    const map: Record<string, FormFieldWithValues> = {};
    fields.forEach((f) => {
      if (f.contact_field_map === 'socio_professional_category') {
        map[getRoleKey(f)] = f;
      }
    });
    return map;
  }, [fields]);

  const newsletterFieldsByRole = useMemo(() => {
    const map: Record<string, FormFieldWithValues> = {};
    fields.forEach((f) => {
      if (f.contact_field_map === 'newsletter_opt_in') {
        map[getRoleKey(f)] = f;
      }
    });
    return map;
  }, [fields]);

  // Initialiser les champs newsletter à "true" (coché par défaut)
  useEffect(() => {
    Object.values(newsletterFieldsByRole).forEach((field) => {
      setValue(field.id, NEWSLETTER_TRUE_VALUE);
    });
  }, [newsletterFieldsByRole, setValue]);

  // Watch all values to react to is_not_student changes
  const watchedValues = watch();
  const isNotStudentByRole = useMemo(() => {
    const result: Record<string, boolean> = {};
    Object.entries(studentFieldsByRole).forEach(([role, field]) => {
      result[role] = watchedValues[field.id] === STUDENT_TRUE_VALUE;
    });
    return result;
  }, [studentFieldsByRole, watchedValues]);

  const handleNewsletterToggle = (field: FormFieldWithValues, checked: boolean) => {
    setValue(field.id, checked ? NEWSLETTER_TRUE_VALUE : NEWSLETTER_FALSE_VALUE, {
      shouldValidate: true,
    });
  };

  /**
   * Toggle de la checkbox "Je ne suis plus étudiant(e)".
   * Stocke "Jeune Actif" / "Étudiant" pour rester compatible avec le trigger
   * SQL existant, vide le champ école du même rôle si l'utilisateur passe en
   * mode "non étudiant", et vide la profession au retour en mode étudiant
   * (le champ est masqué : une valeur résiduelle partirait dans le payload et
   * le trigger 00171 forcerait is_not_student = TRUE).
   */
  const handleStudentToggle = (field: FormFieldWithValues, checked: boolean) => {
    const value = checked ? STUDENT_TRUE_VALUE : STUDENT_FALSE_VALUE;
    setValue(field.id, value, { shouldValidate: true });
    const roleKey = getRoleKey(field);
    if (checked) {
      const schoolField = schoolFieldsByRole[roleKey];
      if (schoolField) {
        setValue(schoolField.id, '', { shouldValidate: true });
      }
    } else {
      const professionField = professionFieldsByRole[roleKey];
      if (professionField) {
        setValue(professionField.id, '', { shouldValidate: true });
        clearErrors(professionField.id);
      }
    }
  };

  const responsiveControlClass = isResponsiveIframeMode
    ? 'h-11 text-base sm:h-10 sm:text-sm'
    : undefined;
  const responsiveLabelClass = isResponsiveIframeMode ? 'text-base sm:text-sm' : undefined;
  const responsiveCheckboxClass = isResponsiveIframeMode ? 'size-5 sm:size-4' : undefined;

  /**
   * TICKET-PRE28-02 — case « Je ne connais pas encore mon copilote ». Cochée :
   * la partie copilote est masquée et n'est plus obligatoire. Les valeurs déjà
   * saisies restent dans le formulaire (retrouvées si on décoche) mais ne sont
   * jamais envoyées.
   */
  function handleCopilotUnknownToggle(checked: boolean) {
    setCopilotUnknown(checked);
    setServerRefusal(null);
    if (checked) {
      clearErrors(fields.filter((f) => f.target_role === 'copilot').map((f) => f.id));
    }
  }

  // =========================================================================
  // RENDER - LOADING STATE
  // =========================================================================

  if (loading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center',
          isResponsiveIframeMode
            ? 'min-h-80 bg-white px-4 py-8'
            : isFullscreen
              ? 'min-h-full bg-white'
              : 'min-h-screen bg-zinc-50'
        )}
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400 mx-auto mb-4" />
          <p className="text-sm text-zinc-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER - ERROR STATE
  // =========================================================================

  if (error || !form) {
    return (
      <div
        className={cn(
          'flex items-center justify-center',
          isResponsiveIframeMode
            ? 'min-h-80 bg-white px-4 py-8'
            : isFullscreen
              ? 'min-h-full bg-white p-6'
              : 'min-h-screen bg-zinc-50 p-6'
        )}
      >
        <div
          className={cn(
            'text-center',
            isResponsiveIframeMode || isFullscreen
              ? 'w-full'
              : 'w-full max-w-md rounded-lg border border-zinc-200 bg-white p-8'
          )}
        >
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-semibold text-zinc-900 mb-2">
            {error || 'Formulaire introuvable'}
          </h1>
          <p className="text-zinc-600 mb-6">
            Le formulaire que vous recherchez n'existe pas ou a été désactivé.
          </p>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER - BED : wizard multi-étapes dédié
  // =========================================================================
  // Le bulletin d'engagement définitif n'est PAS un long formulaire à plat :
  // il est rendu par `BedWizard` (6 étapes, frise, auto-save serveur, options,
  // signatures). Cette branche ne s'active QUE pour un formulaire
  // `generates_bed` ouvert avec son lien tokenisé — les formulaires BPI
  // classiques conservent strictement le rendu ci-dessous.

  if (form.generates_bed && bedAccessToken && fields.length > 0) {
    return (
      <div
        className={
          isFullscreen || isIframeMode ? 'bg-white p-4' : 'min-h-screen bg-zinc-50 px-4 py-8'
        }
      >
        <div className={isFullscreen || isIframeMode ? 'w-full' : 'mx-auto max-w-3xl'}>
          <div className="mb-5">
            <h1
              className={`font-semibold text-zinc-900 ${isFullscreen ? 'text-xl' : 'text-2xl sm:text-3xl'}`}
            >
              {form.name}
            </h1>
            {form.product && (
              <p className="text-sm text-zinc-600">
                Produit : <span className="font-medium">{form.product.name}</span>
              </p>
            )}
          </div>

          <BedWizard
            form={form}
            fields={fields}
            token={bedAccessToken}
            isEmbedded={isFullscreen || isIframeMode}
          />
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER - SUCCESS STATE
  // En mode iframe : positionné à la même hauteur que le bouton Envoyer
  // =========================================================================

  if (submitted) {
    // En mode iframe historique (cadre de hauteur fixe), utiliser la position
    // du bouton capturée pour afficher le message légèrement au-dessus de là où
    // l'utilisateur a cliqué. On remonte de 100px pour s'assurer que le message
    // soit bien visible. En V2, le cadre suit le contenu : pas de décalage (il
    // laisserait un grand blanc), le site remonte au formulaire (voir onSubmit).
    const iframeStyle =
      isIframeMode && !isResponsiveIframeMode
        ? { paddingTop: Math.max(0, submitButtonPosition - 100) }
        : undefined;

    return (
      <div
        className={cn(
          'flex items-start justify-center',
          isResponsiveIframeMode
            ? 'bg-white px-4 py-6 sm:px-6 sm:py-8'
            : isFullscreen || isIframeMode
              ? 'bg-white p-6'
              : 'min-h-screen bg-zinc-50 p-6'
        )}
        style={iframeStyle}
      >
        <div
          className={cn(
            'text-center',
            isFullscreen || isIframeMode
              ? 'w-full'
              : 'w-full max-w-md rounded-lg border border-zinc-200 bg-white p-8'
          )}
        >
          <div className="text-green-600 text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-semibold text-zinc-900 mb-2">Merci !</h1>
          <p className="text-zinc-600 mb-6">
            Votre formulaire a été envoyé avec succès. <br /> Nous vous contacterons prochainement.
          </p>
          {alreadyPaid && form?.generates_bpi && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-emerald-800 text-sm font-medium">
                Vous êtes déjà inscrit(e) : votre acompte a bien été réglé, il n'y a rien de plus à
                payer.
              </p>
              <p className="text-emerald-700 text-sm mt-1">
                Une question sur votre dossier ? Écrivez-nous à{' '}
                <a
                  href="mailto:service.administratif@desertours.com"
                  className="underline font-medium"
                >
                  service.administratif@desertours.com
                </a>
                .
              </p>
            </div>
          )}
          {paymentFailed && form?.generates_bpi && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-amber-800 text-sm font-medium">
                Votre inscription a bien été enregistrée, mais le paiement n'a pas pu être initié
                automatiquement.
              </p>
              <p className="text-amber-700 text-sm mt-1">
                Contactez-nous à{' '}
                <a
                  href="mailto:service.administratif@desertours.com"
                  className="underline font-medium"
                >
                  service.administratif@desertours.com
                </a>{' '}
                pour finaliser votre paiement.
              </p>
            </div>
          )}
          {bedError === 'no_parent' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-amber-800 text-sm font-medium">
                Votre bulletin a bien été enregistré, mais aucun acompte (BPI) réglé n'a été
                retrouvé pour votre équipage : le solde ne peut pas être calculé automatiquement.
              </p>
              <p className="text-amber-700 text-sm mt-1">
                Contactez-nous à{' '}
                <a
                  href="mailto:service.administratif@desertours.com"
                  className="underline font-medium"
                >
                  service.administratif@desertours.com
                </a>{' '}
                pour régulariser votre dossier.
              </p>
            </div>
          )}
          {bedError === 'failed' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-amber-800 text-sm font-medium">
                Votre bulletin a bien été enregistré, mais le paiement du solde n'a pas pu être
                initié automatiquement.
              </p>
              <p className="text-amber-700 text-sm mt-1">
                Contactez-nous à{' '}
                <a
                  href="mailto:service.administratif@desertours.com"
                  className="underline font-medium"
                >
                  service.administratif@desertours.com
                </a>{' '}
                pour finaliser votre paiement.
              </p>
            </div>
          )}
          <Button
            onClick={handleNewSubmission}
            variant="outline"
            className={cn(isResponsiveIframeMode && 'h-11 px-5 text-base sm:h-10 sm:text-sm')}
          >
            Soumettre à nouveau
          </Button>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER - FORM
  // =========================================================================

  /** Rendu d'un champ du formulaire à plat (règles étudiant / profession incluses). */
  function renderField(field: FormFieldWithValues) {
    const roleKey = getRoleKey(field);
    const isStudentField = field.contact_field_map === 'is_not_student';
    const isSchoolField = field.contact_field_map === 'school';
    const isProfessionField = field.contact_field_map === 'socio_professional_category';
    const isNewsletterField = field.contact_field_map === 'newsletter_opt_in';
    const isNotStudentForRole = isNotStudentByRole[roleKey] === true;

    // Item #2 : masquer le champ école si "je ne suis plus étudiant"
    // est coché pour le rôle correspondant.
    if (isSchoolField && isNotStudentForRole) {
      return null;
    }

    // Masquer la catégorie socio-professionnelle si "je ne suis plus
    // étudiant" n'est PAS coché pour le rôle correspondant.
    if (isProfessionField && !isNotStudentForRole) {
      return null;
    }

    // Champ newsletter : rendu inline checkbox + label sans wrapper heading
    if (isNewsletterField) {
      return (
        <div key={field.id} className="flex items-center gap-2">
          <input
            type="checkbox"
            id={field.id}
            checked={watchedValues[field.id] !== NEWSLETTER_FALSE_VALUE}
            onChange={(e) => handleNewsletterToggle(field, e.target.checked)}
            className={cn(
              'rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900',
              responsiveCheckboxClass ?? 'size-4'
            )}
          />
          <label
            htmlFor={field.id}
            className={cn('cursor-pointer text-zinc-600', responsiveLabelClass ?? 'text-sm')}
          >
            {field.label}
          </label>
        </div>
      );
    }

    // Item #2 : marquer profession comme requise visuellement si
    // l'utilisateur n'est plus étudiant pour ce rôle.
    const showRequiredAsterisk =
      requiredFieldIds.has(field.id) || (isProfessionField && isNotStudentForRole);

    const birthDateValue = watchedValues[field.id] as string;
    const showDerogationPopover =
      form?.generates_bpi === true &&
      field.contact_field_map === 'birth_date' &&
      field.field_type === 'date' &&
      isOver28AtEventDate(birthDateValue, productStartDate) &&
      dismissedDerogationValues[field.id] !== birthDateValue;

    return (
      <div key={field.id} className="space-y-2">
        <label
          htmlFor={field.id}
          className={cn('block font-medium text-zinc-900', responsiveLabelClass ?? 'text-sm')}
        >
          {field.label}
          {showRequiredAsterisk && <span className="text-red-500 ml-1">*</span>}
        </label>

        {/* Wrapper relatif pour positionner la popover sans affecter le flux */}
        <div className="relative">
          {/* Item #2 — checkbox "Je ne suis plus étudiant(e)" */}
          {isStudentField ? (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={field.id}
                checked={watchedValues[field.id] === STUDENT_TRUE_VALUE}
                onChange={(event) => handleStudentToggle(field, event.target.checked)}
                className={cn(
                  'rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900',
                  responsiveCheckboxClass ?? 'size-4'
                )}
              />
              <label
                htmlFor={field.id}
                className={cn('text-zinc-600', responsiveLabelClass ?? 'text-sm')}
              >
                Je ne suis plus étudiant(e)
              </label>
            </div>
          ) : isSchoolField ? (
            /* Item #3 — autocomplete écoles */
            <SchoolAutocomplete
              id={field.id}
              value={(watchedValues[field.id] as string) || ''}
              onChange={(value) => setValue(field.id, value, { shouldValidate: true })}
              placeholder={field.placeholder || 'Tapez le nom de votre école…'}
              hasError={Boolean(errors[field.id])}
              inputClassName={responsiveControlClass}
            />
          ) : field.field_type === 'textarea' ? (
            <textarea
              id={field.id}
              {...register(field.id)}
              placeholder={field.placeholder || ''}
              className={cn(
                'w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900',
                isResponsiveIframeMode ? 'min-h-28 text-base sm:text-sm' : 'min-h-[100px]'
              )}
            />
          ) : field.field_type === 'select' && field.options ? (
            <RadixSelect
              value={(watch(field.id) as string) || ''}
              onChange={(value) => setValue(field.id, value)}
              options={field.options.map(
                (option): RadixSelectOption => ({
                  value: option,
                  label: getOptionLabel(option, field.contact_field_map),
                })
              )}
              placeholder="Sélectionnez une option"
              disablePortal={true}
              className={responsiveControlClass}
            />
          ) : field.field_type === 'checkbox' ? (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={field.id}
                {...register(field.id)}
                className={cn(
                  'rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900',
                  responsiveCheckboxClass ?? 'size-4'
                )}
              />
              <label
                htmlFor={field.id}
                className={cn('text-zinc-600', responsiveLabelClass ?? 'text-sm')}
              >
                {field.placeholder || "J'accepte"}
              </label>
            </div>
          ) : field.field_type === 'phone' ? (
            <PhoneInputIframe
              value={(watch(field.id) as string) || ''}
              onChange={(value) => setValue(field.id, value)}
              placeholder={field.placeholder || '6 12 34 56 78'}
              controlClassName={responsiveControlClass}
            />
          ) : (
            <input
              type={
                field.field_type === 'email'
                  ? 'email'
                  : field.field_type === 'number'
                    ? 'number'
                    : field.field_type === 'date'
                      ? 'date'
                      : 'text'
              }
              id={field.id}
              {...register(field.id)}
              placeholder={field.placeholder || ''}
              className={cn(
                'h-9 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900',
                responsiveControlClass
              )}
            />
          )}

          {/* Popover dérogation : flottante en mode historique (cadre de hauteur
              fixe, elle ne pousse pas le bouton Envoyer) ; dans le flux en V2, où
              le cadre suit la hauteur réelle du formulaire (sinon elle serait coupée
              sous le dernier champ). */}
          {showDerogationPopover && (
            <DerogationNotice
              className={
                isResponsiveIframeMode
                  ? 'relative mt-2'
                  : 'absolute left-0 right-0 top-full z-50 mt-1 shadow-md'
              }
              onDismiss={() =>
                setDismissedDerogationValues((prev) => ({
                  ...prev,
                  [field.id]: birthDateValue,
                }))
              }
            />
          )}
        </div>

        {errors[field.id] && (
          <p className={cn('text-red-600', responsiveLabelClass ?? 'text-sm')}>
            {errors[field.id]?.message as string}
          </p>
        )}
      </div>
    );
  }

  // TICKET-PRE28-02 : case placée juste avant le premier champ copilote.
  // Premier champ copilote : délimiteur « Votre copilote » (V2) puis case
  // PRE28-02, quelle que soit l'option (un seul repère pour les deux).
  const firstCopilotId = fields.find((f) => f.target_role === 'copilot')?.id;
  const copilotToggle = (
    <div className="flex items-start gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3">
      <input
        type="checkbox"
        id="copilot-unknown"
        checked={copilotUnknown}
        onChange={(event) => handleCopilotUnknownToggle(event.target.checked)}
        className={cn(
          'mt-0.5 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900',
          responsiveCheckboxClass ?? 'size-4'
        )}
      />
      <label
        htmlFor="copilot-unknown"
        className={cn(
          'cursor-pointer font-medium text-zinc-900',
          responsiveLabelClass ?? 'text-sm'
        )}
      >
        Je ne connais pas encore mon copilote
      </label>
    </div>
  );

  return (
    <div
      className={cn(
        isResponsiveIframeMode
          ? 'bg-white px-4 py-6 sm:px-6 sm:py-8'
          : isFullscreen
            ? 'bg-white p-6'
            : 'min-h-screen bg-zinc-50 px-6 py-12'
      )}
    >
      <div
        className={cn(
          isResponsiveIframeMode
            ? 'mx-auto w-full max-w-2xl'
            : isFullscreen
              ? 'w-full'
              : 'mx-auto max-w-2xl'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            isResponsiveIframeMode
              ? 'mb-5'
              : isFullscreen
                ? 'mb-4'
                : 'mb-6 rounded-lg border border-zinc-200 bg-white p-8'
          )}
        >
          <h1
            className={cn(
              'mb-2 font-semibold text-zinc-900',
              isResponsiveIframeMode
                ? 'text-2xl sm:text-3xl'
                : isFullscreen
                  ? 'text-xl'
                  : 'text-3xl'
            )}
          >
            {form.name}
          </h1>
          {form.product && (
            <p className="text-sm text-zinc-600">
              Produit : <span className="font-medium">{form.product.name}</span>
            </p>
          )}
        </div>

        {/* Form */}
        {/* Formulaire de pré-inscription 4L : tous les contrôles passent par le
            schéma, comme côté serveur. La validation native du navigateur est
            coupée, sinon un email collé avec des espaces insécables serait
            bloqué par le navigateur alors que le CRM l'accepte une fois
            normalisé. */}
        <form
          noValidate={guardedForm}
          onSubmit={handleSubmit(onSubmit, () => setServerRefusal(null))}
          onChangeCapture={handleFormFirstInteraction}
          className={cn(
            isResponsiveIframeMode
              ? 'space-y-5'
              : isFullscreen
                ? 'space-y-4'
                : 'space-y-6 rounded-lg border border-zinc-200 bg-white p-8'
          )}
        >
          {fields.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">
              Ce formulaire ne contient aucun champ pour le moment.
            </p>
          ) : (
            fields.map((field) => (
              <Fragment key={field.id}>
                {field.id === firstCopilotId && (
                  <>
                    {isResponsiveIframeMode && <CopilotSectionDivider />}
                    {copilotOptionEnabled && copilotToggle}
                  </>
                )}
                {!(copilotHidden && field.target_role === 'copilot') && renderField(field)}
              </Fragment>
            ))
          )}

          {serverRefusal && (
            <div
              role="alert"
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
            >
              {serverRefusal === 'copilot'
                ? 'Les informations du copilote sont incomplètes ou invalides. Vérifiez tous les champs du copilote, ou cochez « Je ne connais pas encore mon copilote ».'
                : 'Certaines informations sont invalides : vérifiez les dates de naissance (date passée) et les adresses email.'}
            </div>
          )}

          <Button
            ref={submitButtonRef}
            type="submit"
            disabled={isSubmitting || isProcessing || fields.length === 0}
            className={cn('w-full', isResponsiveIframeMode && 'h-11 text-base sm:h-10 sm:text-sm')}
          >
            {isSubmitting || isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              'Envoyer'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
